/** WF設計(hr:admin)。テンプレートと、その中身(セクション・SaaS項目・工程・入力欄)。 */
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Flex,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";

import type { CatalogItem, CustomField, TemplateField, TemplateStep, WfTemplate } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import {
  EMP_SOURCES,
  FIELD_TYPES,
  STEP_FULFILLMENTS,
  WF_ACTIONS,
  WF_KINDS,
  kindLabel,
} from "./constants";
import { errorText, useCustomFields } from "./hooks";
import {
  useCatalog,
  useInvalidateWf,
  usePartners,
  useTemplate,
  useTemplates,
  useWfPerms,
} from "./wfHooks";

type ItemRow = {
  item_id: number;
  name: string;
  section_id: number | null;
  required: boolean;
  sort: number;
  checked: boolean;
  fulfillment_label: string;
};

type FieldRow = {
  key: string;
  name: string;
  ftype: string;
  options: string;
  section_id: number | null;
  required: boolean;
  /** 転記元("" か "extra:<カスタム項目の key>") */
  source: string;
};

/** 行を 1 つ上(dir = -1)か下(dir = 1)へ。端では動かさない。 */
function moveRow<T extends { key: string }>(rows: T[], key: string, dir: -1 | 1): T[] {
  const i = rows.findIndex((x) => x.key === key);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= rows.length) return rows;
  const next = [...rows];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

export function WorkflowDesignPage() {
  const api = useApi();
  const invalidate = useInvalidateWf();
  const { canAdmin } = useWfPerms();
  const templates = useTemplates();
  const catalog = useCatalog();
  const partners = usePartners(true);
  const [selected, setSelected] = useState<number | undefined>();
  const tpl = useTemplate(selected);

  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newKind, setNewKind] = useState("onboard");

  const create = useMutation({
    mutationFn: () => api.createTemplate({ name: newName, kind: newKind }),
    onSuccess: (t) => {
      void message.success(`「${t.name}」を作った`);
      setCreating(false);
      setNewName("");
      setSelected(t.id);
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const columns: ColumnsType<WfTemplate> = [
    { title: "名前", width: 160, dataIndex: "name" },
    { title: "種別", width: 160, render: (_: unknown, t: WfTemplate) => kindLabel(t.kind) },
    {
      title: "既定",
      width: 90,
      render: (_: unknown, t: WfTemplate) => (t.is_default ? <Tag color="blue">既定</Tag> : null),
    },
  ];

  return (
    <div>
      <Flex align="center" justify="space-between" style={{ marginBottom: 16 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          WF設計
        </Typography.Title>
        {canAdmin && (
          <Button type="primary" onClick={() => setCreating(true)}>
            テンプレートを作る
          </Button>
        )}
      </Flex>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          テンプレートは申請時にスナップショットされる。名前や中身を直しても、過去の申請の見た目は変わらない。
          SaaS はカタログから選び(自動発行はコネクタ経由)、端末準備や入館カードなどは「工程」として定義する。
        </Typography.Text>
      </Card>

      {templates.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="テンプレートを取得できなかった"
          description={errorText(templates.error)}
        />
      )}

      <Flex gap={16} align="flex-start" wrap>
        <Card size="small" title="テンプレート" style={{ minWidth: 340 }}>
          <Table<WfTemplate>
            scroll={{ x: "max-content" }}
            rowKey="id"
            size="small"
            columns={columns}
            dataSource={templates.data?.items ?? []}
            loading={templates.isFetching}
            pagination={false}
            rowClassName={(r) => (r.id === selected ? "ant-table-row-selected" : "")}
            onRow={(r) => ({ onClick: () => setSelected(r.id), style: { cursor: "pointer" } })}
          />
        </Card>

        <div style={{ flex: 1, minWidth: 520 }}>
          {!selected && (
            <Card size="small">
              <Typography.Text type="secondary">左のテンプレートを選ぶ</Typography.Text>
            </Card>
          )}
          {selected && tpl.data && (
            <TemplateEditor
              tpl={tpl.data}
              canAdmin={canAdmin}
              catalog={catalog.data?.items ?? []}
              partners={partners.data?.items ?? []}
              onDeleted={() => setSelected(undefined)}
            />
          )}
        </div>
      </Flex>

      <Modal
        open={creating}
        title="テンプレートを作る"
        okText="作る"
        cancelText="やめる"
        confirmLoading={create.isPending}
        onCancel={() => setCreating(false)}
        onOk={() => create.mutate()}
      >
        <Space direction="vertical" style={{ width: "100%", marginTop: 12 }}>
          <Input
            placeholder="テンプレート名"
            value={newName}
            maxLength={200}
            onChange={(e) => setNewName(e.target.value)}
          />
          <Select
            style={{ width: "100%" }}
            value={newKind}
            onChange={setNewKind}
            options={WF_KINDS}
          />
        </Space>
      </Modal>
    </div>
  );
}

function TemplateEditor({
  tpl,
  canAdmin,
  catalog,
  partners,
  onDeleted,
}: {
  tpl: WfTemplate;
  canAdmin: boolean;
  catalog: CatalogItem[];
  partners: { id: number; name: string }[];
  onDeleted: () => void;
}) {
  const api = useApi();
  const invalidate = useInvalidateWf();
  const [name, setName] = useState(tpl.name);
  const [note, setNote] = useState(tpl.note);
  const [sectionName, setSectionName] = useState("");
  const [stepModal, setStepModal] = useState(false);
  const [step, setStep] = useState<Partial<TemplateStep>>({});
  const [items, setItems] = useState<ItemRow[]>([]);
  const [fields, setFields] = useState<FieldRow[]>([]);
  // 入力欄の転記元の候補(停止中の項目も、既に指している欄のために出す)
  const customFields = useCustomFields(false);
  // 選択の欄の転記元が選択式のカスタム項目なら、選択肢はその項目から借りる(自前の選択肢は使わない)
  const borrowedFrom = (r: FieldRow): CustomField | undefined => {
    if (r.ftype !== "select" || !r.source.startsWith("extra:")) return undefined;
    const c = customFields.data?.items.find((x) => `extra:${x.key}` === r.source);
    return c?.type === "select" ? c : undefined;
  };

  useEffect(() => {
    setName(tpl.name);
    setNote(tpl.note);
    setItems(
      catalog.map((c) => {
        const cur = tpl.items.find((i) => i.item_id === c.id);
        return {
          item_id: c.id,
          name: c.name,
          fulfillment_label: c.fulfillment_label,
          section_id: cur?.section_id ?? null,
          required: cur?.required ?? false,
          sort: cur?.sort ?? 0,
          checked: !!cur,
        };
      }),
    );
    setFields(
      tpl.fields.map((f: TemplateField) => ({
        key: String(f.id),
        name: f.name,
        ftype: f.ftype,
        options: (f.options ?? []).join(", "),
        section_id: f.section_id,
        required: f.required,
        source: f.source ?? "",
      })),
    );
  }, [tpl, catalog]);

  const run = (fn: () => Promise<unknown>, ok: string) =>
    fn().then(
      () => {
        void message.success(ok);
        invalidate();
      },
      (e: unknown) => void message.error(errorText(e)),
    );

  const sectionOptions = [
    { value: 0, label: "(親なし)" },
    ...tpl.sections.map((s) => ({ value: s.id, label: s.name })),
  ];

  const sectionColumns: ColumnsType<{ id: number; name: string; sort: number }> = [
    { title: "名前", width: 160, dataIndex: "name" },
    { title: "並び", dataIndex: "sort", width: 90 },
    ...(canAdmin
      ? [
          {
            title: "",
            width: 90,
            render: (_: unknown, s: { id: number; name: string }) => (
              <Popconfirm
                title={`「${s.name}」を消す`}
                description="中身(入力欄・SaaS・工程)が残っているセクションは消せない"
                onConfirm={() =>
                  void run(() => api.deleteSection(tpl.id, s.id), "セクションを消した")
                }
              >
                <Button size="small" danger>
                  削除
                </Button>
              </Popconfirm>
            ),
          },
        ]
      : []),
  ];

  const stepColumns: ColumnsType<TemplateStep> = [
    { title: "セクション", dataIndex: "section", width: 160 },
    { title: "工程", width: 160, dataIndex: "name" },
    {
      title: "方式",
      width: 360,
      render: (_: unknown, s: TemplateStep) =>
        s.fulfillment === "external"
          ? `社外依頼(${partners.find((p) => p.id === s.partner_id)?.name ?? "依頼先なし"})`
          : `手作業(${s.default_owner || "担当未設定"})`,
    },
    {
      title: "向き",
      width: 160,
      render: (_: unknown, s: TemplateStep) =>
        WF_ACTIONS.find((a) => a.value === s.action)?.label ?? s.action,
    },
    {
      title: "印",
      width: 160,
      render: (_: unknown, s: TemplateStep) => (
        <Space size={4}>
          {s.required && <Tag color="red">必須</Tag>}
          {s.loan && <Tag color="blue">貸与</Tag>}
        </Space>
      ),
    },
    { title: "並び", dataIndex: "sort", width: 90 },
    ...(canAdmin
      ? [
          {
            title: "",
            width: 90,
            render: (_: unknown, s: TemplateStep) => (
              <Popconfirm
                title={`「${s.name}」を消す`}
                onConfirm={() => void run(() => api.deleteStep(tpl.id, s.id), "工程を消した")}
              >
                <Button size="small" danger>
                  削除
                </Button>
              </Popconfirm>
            ),
          },
        ]
      : []),
  ];

  const itemColumns: ColumnsType<ItemRow> = [
    {
      title: "使う",
      width: 90,
      render: (_: unknown, r: ItemRow) => (
        <Checkbox
          checked={r.checked}
          disabled={!canAdmin}
          onChange={(e) =>
            setItems((p) =>
              p.map((x) => (x.item_id === r.item_id ? { ...x, checked: e.target.checked } : x)),
            )
          }
        />
      ),
    },
    { title: "SaaS", width: 160, dataIndex: "name" },
    { title: "発行方式", dataIndex: "fulfillment_label", width: 160 },
    {
      title: "セクション",
      width: 190,
      render: (_: unknown, r: ItemRow) => (
        <Select
          size="small"
          style={{ width: 150 }}
          disabled={!canAdmin || !r.checked}
          value={r.section_id ?? 0}
          onChange={(v: number) =>
            setItems((p) =>
              p.map((x) => (x.item_id === r.item_id ? { ...x, section_id: v || null } : x)),
            )
          }
          options={sectionOptions}
        />
      ),
    },
    {
      title: "必須",
      width: 90,
      render: (_: unknown, r: ItemRow) => (
        <Checkbox
          checked={r.required}
          disabled={!canAdmin || !r.checked}
          onChange={(e) =>
            setItems((p) =>
              p.map((x) => (x.item_id === r.item_id ? { ...x, required: e.target.checked } : x)),
            )
          }
        />
      ),
    },
    {
      title: "並び",
      width: 90,
      render: (_: unknown, r: ItemRow) => (
        <InputNumber
          size="small"
          style={{ width: 70 }}
          disabled={!canAdmin || !r.checked}
          value={r.sort}
          onChange={(v) =>
            setItems((p) =>
              p.map((x) => (x.item_id === r.item_id ? { ...x, sort: Number(v ?? 0) } : x)),
            )
          }
        />
      ),
    },
  ];

  const fieldColumns: ColumnsType<FieldRow> = [
    {
      title: "項目名",
      width: 200,
      render: (_: unknown, r: FieldRow) => (
        <Input
          size="small"
          value={r.name}
          disabled={!canAdmin}
          onChange={(e) =>
            setFields((p) => p.map((x) => (x.key === r.key ? { ...x, name: e.target.value } : x)))
          }
        />
      ),
    },
    {
      title: "型",
      width: 160,
      render: (_: unknown, r: FieldRow) => (
        <Select
          size="small"
          style={{ width: 110 }}
          disabled={!canAdmin}
          value={r.ftype}
          onChange={(v: string) =>
            setFields((p) => p.map((x) => (x.key === r.key ? { ...x, ftype: v } : x)))
          }
          // 従業員マスタの列を転記する欄はサーバーが埋める(申請者は直せない)ので自由入力に限る
          options={r.source.startsWith("emp:") ? FIELD_TYPES.filter((t) => t.value === "text") : FIELD_TYPES}
        />
      ),
    },
    {
      title: "選択肢(, 区切り)",
      width: 480,
      ellipsis: true,
      onCell: (row) => ({ title: row.ftype === "employee" ? "従業員一覧から選ぶ(検索つき)。値は氏名" : row.source.startsWith("emp:") ? "申請時にサーバーが対象者の値で埋める(入力欄は出ない)" : borrowedFrom(row) ? `従業員の「${borrowedFrom(row)!.label}」の選択肢(${borrowedFrom(row)!.options.length} 件)を使う` : row.options }),
      render: (_: unknown, r: FieldRow) => {
        if (r.ftype === "employee") {
          return (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              従業員一覧から選ぶ(検索つき)。値は氏名
            </Typography.Text>
          );
        }
        if (r.source.startsWith("emp:")) {
          return (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              申請時にサーバーが対象者の値で埋める(入力欄は出ない)
            </Typography.Text>
          );
        }
        const c = borrowedFrom(r);
        if (c) {
          return (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              従業員の「{c.label}」の選択肢({c.options.length} 件)を使う
            </Typography.Text>
          );
        }
        return (
          <Input
            size="small"
            value={r.options}
            disabled={!canAdmin || r.ftype !== "select"}
            onChange={(e) =>
              setFields((p) => p.map((x) => (x.key === r.key ? { ...x, options: e.target.value } : x)))
            }
          />
        );
      },
    },
    {
      title: "セクション",
      width: 190,
      render: (_: unknown, r: FieldRow) => (
        <Select
          size="small"
          style={{ width: 150 }}
          disabled={!canAdmin}
          value={r.section_id ?? 0}
          onChange={(v: number) =>
            setFields((p) => p.map((x) => (x.key === r.key ? { ...x, section_id: v || null } : x)))
          }
          options={sectionOptions}
        />
      ),
    },
    {
      // 対象者のカスタム項目の値を申請フォームに最初から入れる(申請者が直せる)。
      // 選択の欄は、値が選択肢に無ければ入れない(選択肢の綴りを従業員側と揃える)
      title: "転記元",
      width: 220,
      render: (_: unknown, r: FieldRow) => (
        <Select
          size="small"
          style={{ width: 170 }}
          allowClear
          placeholder="手入力のみ"
          disabled={!canAdmin}
          value={r.source || undefined}
          onChange={(v: string | undefined) =>
            setFields((p) =>
              p.map((x) =>
                x.key === r.key
                  ? // 従業員マスタの列はサーバーが埋める欄なので、型は自由入力に戻す
                    { ...x, source: v ?? "", ftype: v?.startsWith("emp:") ? "text" : x.ftype }
                  : x,
              ),
            )
          }
          options={[
            {
              label: "従業員マスタ(申請時にサーバーが埋める。直せない)",
              options: EMP_SOURCES.map((e) => ({ value: e.value, label: `従業員の${e.label}` })),
            },
            {
              label: "カスタム項目(最初から入る。申請者が直せる)",
              options: (customFields.data?.items ?? []).map((c) => ({
                value: `extra:${c.key}`,
                label: `従業員の${c.label}${c.active ? "" : "(停止中)"}`,
              })),
            },
          ]}
        />
      ),
    },
    {
      title: "必須",
      width: 90,
      render: (_: unknown, r: FieldRow) => (
        <Checkbox
          checked={r.required}
          disabled={!canAdmin}
          onChange={(e) =>
            setFields((p) =>
              p.map((x) => (x.key === r.key ? { ...x, required: e.target.checked } : x)),
            )
          }
        />
      ),
    },
    ...(canAdmin
      ? [
          {
            // 並びは配列の順(保存時に sort へ写る)。↑↓ で隣と入れ替える
            title: "並び",
            width: 180,
            render: (_: unknown, r: FieldRow) => {
              const i = fields.findIndex((x) => x.key === r.key);
              return (
                <Space size={2}>
                  <Button
                    size="small"
                    aria-label={`「${r.name || "(無題)"}」を上へ`}
                    disabled={i <= 0}
                    onClick={() => setFields((p) => moveRow(p, r.key, -1))}
                  >
                    ↑
                  </Button>
                  <Button
                    size="small"
                    aria-label={`「${r.name || "(無題)"}」を下へ`}
                    disabled={i < 0 || i >= fields.length - 1}
                    onClick={() => setFields((p) => moveRow(p, r.key, 1))}
                  >
                    ↓
                  </Button>
                </Space>
              );
            },
          },
          {
            title: "",
            width: 90,
            render: (_: unknown, r: FieldRow) => (
              <Button
                size="small"
                danger
                onClick={() => setFields((p) => p.filter((x) => x.key !== r.key))}
              >
                外す
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <Card
      size="small"
      title={
        <Space>
          <span>{tpl.name}</span>
          <Tag>{kindLabel(tpl.kind)}</Tag>
          {tpl.is_default && <Tag color="blue">既定</Tag>}
        </Space>
      }
      extra={
        canAdmin && (
          <Space>
            {!tpl.is_default && (
              <Button
                size="small"
                onClick={() => void run(() => api.setDefaultTemplate(tpl.id), "既定にした")}
              >
                既定にする
              </Button>
            )}
            <Button
              size="small"
              onClick={() => void run(() => api.duplicateTemplate(tpl.id), "複製した")}
            >
              複製
            </Button>
            <Popconfirm
              title={`「${tpl.name}」を消す`}
              description="承認待ちの申請が参照していると消せない(過去の申請はスナップショットなので影響しない)"
              onConfirm={() =>
                void api.deleteTemplate(tpl.id).then(
                  () => {
                    void message.success("テンプレートを消した");
                    onDeleted();
                    invalidate();
                  },
                  (e: unknown) => void message.error(errorText(e)),
                )
              }
            >
              <Button size="small" danger>
                削除
              </Button>
            </Popconfirm>
          </Space>
        )
      }
    >
      <Tabs
        size="small"
        items={[
          {
            key: "base",
            label: "基本",
            children: (
              <Space direction="vertical" style={{ width: "100%" }}>
                <Input
                  addonBefore="名前"
                  value={name}
                  disabled={!canAdmin}
                  maxLength={200}
                  onChange={(e) => setName(e.target.value)}
                />
                <Input.TextArea
                  rows={2}
                  placeholder="備考"
                  value={note}
                  disabled={!canAdmin}
                  onChange={(e) => setNote(e.target.value)}
                />
                {canAdmin && (
                  <Button
                    type="primary"
                    onClick={() => void run(() => api.updateTemplate(tpl.id, { name, note }), "保存した")}
                  >
                    保存
                  </Button>
                )}
              </Space>
            ),
          },
          {
            key: "sections",
            label: `セクション(${tpl.sections.length})`,
            children: (
              <Space direction="vertical" style={{ width: "100%" }}>
                <Table
                  scroll={{ x: "max-content" }}
                  rowKey="id"
                  size="small"
                  columns={sectionColumns}
                  dataSource={tpl.sections}
                  pagination={false}
                />
                {canAdmin && (
                  <Space>
                    <Input
                      placeholder="セクション名(例: AM準備)"
                      value={sectionName}
                      onChange={(e) => setSectionName(e.target.value)}
                    />
                    <Button
                      onClick={() =>
                        void run(
                          () =>
                            api
                              .createSection(tpl.id, {
                                name: sectionName,
                                sort: tpl.sections.length,
                              })
                              .then(() => setSectionName("")),
                          "セクションを足した",
                        )
                      }
                    >
                      足す
                    </Button>
                  </Space>
                )}
              </Space>
            ),
          },
          {
            key: "items",
            label: `SaaS(${tpl.items.length})`,
            children: (
              <Space direction="vertical" style={{ width: "100%" }}>
                <Table<ItemRow>
                  scroll={{ x: "max-content" }}
                  rowKey="item_id"
                  size="small"
                  columns={itemColumns}
                  dataSource={items}
                  pagination={false}
                />
                {canAdmin && (
                  <Button
                    type="primary"
                    onClick={() =>
                      void run(
                        () =>
                          api.putTemplateItems(
                            tpl.id,
                            items
                              .filter((i) => i.checked)
                              .map((i) => ({
                                item_id: i.item_id,
                                section_id: i.section_id,
                                required: i.required,
                                sort: i.sort,
                              })),
                          ),
                        "SaaS 項目を保存した",
                      )
                    }
                  >
                    保存(チェックしたものに置き換える)
                  </Button>
                )}
              </Space>
            ),
          },
          {
            key: "steps",
            label: `工程(${tpl.steps.length})`,
            children: (
              <Space direction="vertical" style={{ width: "100%" }}>
                <Table<TemplateStep>
                  scroll={{ x: "max-content" }}
                  rowKey="id"
                  size="small"
                  columns={stepColumns}
                  dataSource={tpl.steps}
                  pagination={false}
                />
                {canAdmin && (
                  <Button
                    onClick={() => {
                      setStep({ fulfillment: "manual", action: "provision", sort: tpl.steps.length });
                      setStepModal(true);
                    }}
                  >
                    工程を足す
                  </Button>
                )}
              </Space>
            ),
          },
          {
            key: "fields",
            label: `入力欄(${tpl.fields.length})`,
            children: (
              <Space direction="vertical" style={{ width: "100%" }}>
                <Table<FieldRow>
                  scroll={{ x: "max-content" }}
                  rowKey="key"
                  size="small"
                  columns={fieldColumns}
                  dataSource={fields}
                  pagination={false}
                />
                {canAdmin && (
                  <Space>
                    <Button
                      onClick={() =>
                        setFields((p) => [
                          ...p,
                          {
                            key: `new-${Date.now()}`,
                            name: "",
                            ftype: "text",
                            options: "",
                            section_id: null,
                            required: false,
                            source: "",
                          },
                        ])
                      }
                    >
                      行を足す
                    </Button>
                    <Button
                      type="primary"
                      onClick={() =>
                        void run(
                          () =>
                            api.putTemplateFields(
                              tpl.id,
                              fields
                                .filter((f) => f.name.trim())
                                .map((f) => ({
                                  name: f.name.trim(),
                                  ftype: f.ftype,
                                  options: f.options
                                    .split(",")
                                    .map((o) => o.trim())
                                    .filter(Boolean),
                                  section_id: f.section_id,
                                  required: f.required,
                                  source: f.source,
                                })),
                            ),
                          "入力欄を保存した",
                        )
                      }
                    >
                      保存(この並びに置き換える)
                    </Button>
                  </Space>
                )}
              </Space>
            ),
          },
        ]}
      />

      <Modal
        open={stepModal}
        title="工程を足す"
        okText="足す"
        cancelText="やめる"
        onCancel={() => setStepModal(false)}
        onOk={() =>
          void run(
            () =>
              api
                .createStep(tpl.id, {
                  name: (step.name ?? "").trim(),
                  fulfillment: step.fulfillment ?? "manual",
                  default_owner: step.default_owner ?? "",
                  partner_id: step.partner_id ?? null,
                  section_id: step.section_id ?? null,
                  required: !!step.required,
                  loan: !!step.loan,
                  action: step.action ?? "provision",
                  sort: step.sort ?? 0,
                })
                .then(() => setStepModal(false)),
            "工程を足した",
          )
        }
      >
        <Space direction="vertical" style={{ width: "100%", marginTop: 12 }}>
          <Input
            placeholder="工程名(例: 入館カード)"
            value={step.name ?? ""}
            onChange={(e) => setStep((p) => ({ ...p, name: e.target.value }))}
          />
          <Select
            style={{ width: "100%" }}
            value={step.fulfillment ?? "manual"}
            onChange={(v: string) => setStep((p) => ({ ...p, fulfillment: v }))}
            options={STEP_FULFILLMENTS}
          />
          {step.fulfillment === "external" ? (
            <Select
              style={{ width: "100%" }}
              placeholder="依頼先(必須)"
              value={step.partner_id ?? undefined}
              onChange={(v: number) => setStep((p) => ({ ...p, partner_id: v }))}
              options={partners.map((p) => ({ value: p.id, label: p.name }))}
            />
          ) : (
            <Input
              placeholder="担当(例: 総務)"
              value={step.default_owner ?? ""}
              onChange={(e) => setStep((p) => ({ ...p, default_owner: e.target.value }))}
            />
          )}
          <Select
            style={{ width: "100%" }}
            value={step.section_id ?? 0}
            onChange={(v: number) => setStep((p) => ({ ...p, section_id: v || null }))}
            options={sectionOptions}
          />
          <Select
            style={{ width: "100%" }}
            value={step.action ?? "provision"}
            onChange={(v: string) => setStep((p) => ({ ...p, action: v }))}
            options={WF_ACTIONS}
          />
          <Space>
            <Checkbox
              checked={!!step.required}
              onChange={(e) => setStep((p) => ({ ...p, required: e.target.checked }))}
            >
              既定でチェック(必須)
            </Checkbox>
            <Checkbox
              checked={!!step.loan}
              onChange={(e) => setStep((p) => ({ ...p, loan: e.target.checked }))}
            >
              貸与品になる
            </Checkbox>
          </Space>
        </Space>
      </Modal>
    </Card>
  );
}
